import { useEffect, useRef } from 'react'

import { useExecuteCommandMutation } from '@redux/api'

import { executeCommandAction } from '@hooks'
import { getValuesFromVariables } from '@helpers'
import { ACTION_CODE, ENTITY, EVENT_CODE } from '@constants'
import { ActionRow, GETAllVariables, ObjectDataRecord } from '@types'

import { EVENT_NAME, LocalEventBus, publish } from '../events'

interface UseChangeVariableActionsProps {
  selectedRows?: ObjectDataRecord[]
  objectData?: ObjectDataRecord | ObjectDataRecord[]
  preFillObjectData?: ObjectDataRecord | ObjectDataRecord[]
  hasExternalActionData?: boolean
  actions?: ActionRow[]
  entityId?: string | number
  outerVariables?: GETAllVariables
  outerEventBus?: LocalEventBus
  type?: ENTITY
  uniqueEventId?: string
}

const evaluateJsValue = (jsValue: string | undefined, context: Record<string, unknown>) => {
  if (!jsValue) return context
  try {
    return new Function('srcObj', jsValue)(context)
  } catch (error) {
    console.error('Error evaluating jsValue script:', error)

    return context
  }
}

const publishChangeVariable = (
  variable: ActionRow['variable'],
  value: unknown,
  entityId?: string | number,
  uniqueEventId?: string
) => {
  const newVariableValue = {
    ...variable,
    entityId,
    value,
    updatedOn: new Date().toISOString(),
  }

  publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariableValue, uniqueEventId)
}

const publishChangeVariableForWindow = (
  jsValue: string,
  value: unknown,
  outerVariables: GETAllVariables,
  outerEventBus?: LocalEventBus
) => {
  const prepareOuterVariables = outerVariables?.reduce(
    (acc: Record<string | number, any>, variable) => {
      acc[variable.internalId] = variable

      return acc
    },
    {}
  )

  const srcObj = { outerVariables: prepareOuterVariables }

  const currentVariable = evaluateJsValue(jsValue, srcObj)

  const newVariable = {
    ...currentVariable,
    defaultValue: value,
    updatedOn: new Date().toISOString(),
  }

  if (outerEventBus) {
    outerEventBus.publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariable)

    return
  }

  publish(EVENT_NAME.CHANGE_VARIABLE_VALUE, newVariable)
}

export const useChangeVariableActions = ({
  selectedRows,
  objectData,
  actions,
  entityId,
  outerVariables,
  outerEventBus,
  type,
  preFillObjectData,
  uniqueEventId,
}: UseChangeVariableActionsProps) => {
  const isInitialRef = useRef(true)

  const [executeCommand] = useExecuteCommandMutation()

  useEffect(() => {
    if (!actions) return

    const rowSelectAction = actions.find(
      action =>
        action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        action.eventCode === EVENT_CODE.ON_ROW_SELECTION
    )

    if (rowSelectAction?.variable) {
      publishChangeVariable(rowSelectAction.variable, selectedRows, entityId, uniqueEventId)
    }

    if (rowSelectAction?.jsValue && outerVariables) {
      publishChangeVariableForWindow(
        rowSelectAction.jsValue,
        selectedRows,
        outerVariables,
        outerEventBus
      )
    }
  }, [selectedRows, actions, entityId, outerEventBus])

  useEffect(() => {
    if (!actions) return

    if (isInitialRef.current && type === ENTITY.LIST_CONTROL) {
      isInitialRef.current = false

      actions.forEach(action => {
        if (action.eventCode !== EVENT_CODE.ON_VARIABLE_CHANGE || !action.variable) return

        const preparedVariables = getValuesFromVariables(outerVariables)

        const variablesIncludesTargetVariable = action.variable?.internalId in preparedVariables

        if (!variablesIncludesTargetVariable) return

        const valueVariable = preparedVariables[action.variable?.internalId]

        if (!valueVariable) return

        publishChangeVariable(action.variable, valueVariable, entityId, uniqueEventId)
      })
    }

    const afterLoadActions = actions.filter(
      actions =>
        actions.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        actions.eventCode === EVENT_CODE.AFTER_LOAD_DATA
    )

    const onInitChangeVariableActionsWithoutCommands: ActionRow[] = []
    const onInitChangeVariableActionWithCommands: ActionRow[] = []

    actions.forEach(action => {
      if (
        action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        action.eventCode === EVENT_CODE.ON_INIT_FORM &&
        !action.command
      ) {
        onInitChangeVariableActionsWithoutCommands.push(action)

        return
      }

      if (
        action.actionCode === ACTION_CODE.CHANGE_VARIABLE_VALUE &&
        action.eventCode === EVENT_CODE.ON_INIT_FORM &&
        action.command
      ) {
        onInitChangeVariableActionWithCommands.push(action)
      }
    })

    // собираем все publish'и для отложенной отправки — подписчики
    // (useSetFormValueOnVariableChange) подписываются в своём useEffect,
    // который может отработать позже текущего
    type PendingPublish = { variable: ActionRow['variable']; value: unknown }
    const pending: PendingPublish[] = []

    afterLoadActions.forEach(action => {
      if (action?.variable) {
        const value = evaluateJsValue(action.jsValue, {
          objectData,
          preFillObjectData,
          outerVariables: outerVariables ? getValuesFromVariables(outerVariables) : {},
        })

        // скрипт не вернул значение — условия не выполнились, переменную не трогаем
        if (action.jsValue && value === undefined) return

        pending.push({ variable: action.variable, value })
      }
    })

    onInitChangeVariableActionsWithoutCommands.forEach(action => {
      if (action?.variable) {
        const value = evaluateJsValue(action.jsValue, {
          row: objectData,
          preFillObjectData,
          objectData,
          outerVariables: outerVariables ? getValuesFromVariables(outerVariables) : {},
        })

        // скрипт не вернул значение — условия не выполнились, переменную не трогаем
        if (action.jsValue && value === undefined) return

        pending.push({ variable: action.variable, value })
      }
    })

    onInitChangeVariableActionWithCommands.forEach(action => {
      if (action?.variable) {
        executeCommandAction({
          action,
          executeCommand,
          context: {
            row: objectData,
            preFillObjectData,
            objectData,
            outerVariables: outerVariables ? getValuesFromVariables(outerVariables) : {},
          },
        }).then(result => {
          const value = evaluateJsValue(action.jsValue, {
            row: objectData,
            preFillObjectData,
            objectData,
            outerVariables: outerVariables ? getValuesFromVariables(outerVariables) : {},
            commandResponse: result,
          })

          // скрипт не вернул значение — условия не выполнились, переменную не трогаем
          if (action.jsValue && value === undefined) {
            return
          }

          publishChangeVariable(action.variable, value, entityId, uniqueEventId)
        })
      }
    })

    const timeoutId = setTimeout(() => {
      pending.forEach(({ variable, value }) => {
        publishChangeVariable(variable, value, entityId, uniqueEventId)
      })
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [objectData, actions, entityId, outerVariables])
}
